/**
 * 同轴心锁定旋转领域规则。
 *
 * 文件职责：
 * 1. 判断 Concentric Mate 是否具备 SOLIDWORKS `Lock rotation` 的可用条件；
 * 2. 只读取可序列化 Mate Definition 与 BRep 描述符，不依赖 Viewer、Three.js 对象或求解器状态；
 * 3. 为 PropertyManager、设计树命令和文档命令提供同一份判定逻辑。
 */

import {
  AssemblyMateType,
  MateEntityKind,
  MateSolveStatus,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from './AssemblyMateDefinition';

/** 可接受的最小轴向长度平方，避免退化方向被误判为可锁定轴线。 */
const MIN_AXIS_DIRECTION_LENGTH_SQUARED = 1e-24;

/**
 * 判断值是否为有限三维坐标。
 *
 * @param value 待判断值。
 * @returns 是否为可序列化三维坐标。
 */
function isFinitePointTuple(value: unknown): value is TopologyPoint3D {
  return Array.isArray(value)
    && value.length === 3
    && value.every((item) => typeof item === 'number' && Number.isFinite(item));
}

/**
 * 判断三维方向是否有有效长度。
 *
 * @param value 待判断方向。
 * @returns 是否为非退化三维方向。
 */
function isUsableDirectionTuple(value: unknown): value is TopologyPoint3D {
  if (!isFinitePointTuple(value)) {
    return false;
  }
  const lengthSquared = value[0] * value[0] + value[1] * value[1] + value[2] * value[2];
  return lengthSquared > MIN_AXIS_DIRECTION_LENGTH_SQUARED;
}

/**
 * 判断实体描述符能否稳定解析为轴线。
 *
 * 设计说明：
 * SOLIDWORKS 的同轴心锁定旋转只对仍有“绕公共轴旋转自由度”的同轴心配合有意义。
 * 球心/点式 Concentric 只能约束中心点，不存在公共旋转轴，因此不能启用锁定旋转。
 *
 * @param entity Mate 参与实体。
 * @returns 是否具备可锁定旋转的轴线。
 */
export function canResolveAssemblyMateLockRotationAxis(entity: MateEntityRef): boolean {
  const geometry = entity.geometry as BRepGeometryDescriptor & Record<string, unknown>;

  if (entity.entityKind === MateEntityKind.Line || entity.entityKind === MateEntityKind.ReferenceAxis) {
    const origin = isFinitePointTuple(geometry.start) || isFinitePointTuple(geometry.origin);
    return origin && isUsableDirectionTuple(geometry.direction);
  }

  if (
    entity.entityKind === MateEntityKind.Cylinder
    || entity.entityKind === MateEntityKind.Cone
    || entity.entityKind === MateEntityKind.CircularEdge
  ) {
    const origin = isFinitePointTuple(geometry.axisOrigin) || isFinitePointTuple(geometry.center);
    const direction = isUsableDirectionTuple(geometry.axisDirection) || isUsableDirectionTuple(geometry.normal);
    return origin && direction;
  }

  return false;
}

/**
 * 判断两个实体是否允许使用同轴心锁定旋转。
 *
 * @param firstEntity 第一选择实体。
 * @param secondEntity 第二选择实体。
 * @returns 是否允许锁定旋转。
 */
export function canLockConcentricMateRotationEntities(
  firstEntity: MateEntityRef,
  secondEntity: MateEntityRef,
): boolean {
  return canResolveAssemblyMateLockRotationAxis(firstEntity)
    && canResolveAssemblyMateLockRotationAxis(secondEntity);
}

/**
 * 同轴心锁定旋转状态阻塞判断输入。
 */
export interface ConcentricLockRotationStatusBlockInput {
  /** 当前文档内已经存在的 Mate Definition。 */
  readonly mateDefinitions: readonly AssemblyMateDefinition[];
  /** 当前 PropertyManager 中被选为同轴心配合的实体。 */
  readonly selectedEntities: readonly MateEntityRef[];
}

/**
 * 读取 Mate Definition 涉及的全部组件实例 ID。
 *
 * @param mate Mate Definition。
 * @returns 去重后的组件实例 ID 集合。
 */
function resolveMateComponentInstanceIds(mate: AssemblyMateDefinition): readonly string[] {
  return Array.from(new Set([
    mate.firstEntity.componentInstanceId,
    mate.secondEntity.componentInstanceId,
    ...(mate.referenceEntity ? [mate.referenceEntity.componentInstanceId] : []),
  ]));
}

/**
 * 判断当前同轴心锁定旋转是否应被已有错误链路阻塞。
 *
 * 设计说明：
 * SOLIDWORKS 不允许对已经 over-defined 的引用组件继续锁定同轴心旋转。这里按组件实例 ID
 * 判断当前选择是否与已提交的过定义 Mate 共享组件，避免 UI 只按几何轴线启用选项后再由求解器失败兜底。
 *
 * @param input 状态阻塞判断输入。
 * @returns 是否应禁用锁定旋转。
 */
export function isConcentricLockRotationBlockedByOverDefinedReference(
  input: ConcentricLockRotationStatusBlockInput,
): boolean {
  const selectedComponentIds = new Set(input.selectedEntities.map((entity) => entity.componentInstanceId));
  return input.mateDefinitions.some((mate) => {
    if (mate.suppressed || mate.solveStatus !== MateSolveStatus.OverDefined) {
      return false;
    }

    return resolveMateComponentInstanceIds(mate).some((componentInstanceId) => (
      selectedComponentIds.has(componentInstanceId)
    ));
  });
}

/**
 * 判断已提交 Mate 是否可由 `Lock Concentric Rotation` 批量命令锁定。
 *
 * @param mate Mate Definition。
 * @returns 是否应被批量锁定命令更新。
 */
export function canLockConcentricMateRotation(mate: AssemblyMateDefinition): boolean {
  if (
    mate.mateType !== AssemblyMateType.Concentric
    || mate.lockRotation === true
    || mate.suppressed
  ) {
    return false;
  }

  /**
   * SOLIDWORKS 明确禁止对 over-defined 引用锁定同轴心旋转。
   * 缺失引用、未支持几何与未求解状态同样不能证明可安全锁定，因此只允许 NotRun/Solved。
   */
  if (mate.solveStatus !== MateSolveStatus.NotRun && mate.solveStatus !== MateSolveStatus.Solved) {
    return false;
  }

  return canLockConcentricMateRotationEntities(mate.firstEntity, mate.secondEntity);
}
